import { useState } from 'react'
import { Column, Cell } from 'react-table'
import { useDisclosure } from '@chakra-ui/react'

import { DataTable } from '@/components/DataDisplay/Table/DataTable'
import RevenueDetailsModal from './RevenueDetailsModal'
import { useRevenueDetails } from '../queries/useUserStats'

type Row = {
  month: string
  small: number
  medium: number
  large: number
  enterprise: number
  revenue: number
  monthIndex: number
}

type Props = {
  rows: Row[]
  isLoading: boolean
}

function formatCurrency(value: number): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function BusinessOverviewTable({ rows, isLoading }: Props) {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null)
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null)

  const {
    data: revenueDetails,
    isLoading: isLoadingDetails,
    refetch,
  } = useRevenueDetails({
    month: selectedMonth ?? undefined,
    package: selectedPackage ?? undefined,
    enabled: isOpen && !!selectedMonth && !!selectedPackage,
  })

  const handleCellClick = (monthIndex: number, packageName: string) => {
    setSelectedMonth(monthIndex)
    setSelectedPackage(packageName)
    onOpen()
  }

  const handleFilterChange = () => {
    refetch()
  }

  const columns: Column<Row>[] = [
    {
      Header: 'Month',
      accessor: 'month',
      disableSortBy: true,
    },
    {
      Header: 'Small Package',
      accessor: 'small',
      disableSortBy: true,
      Cell: ({ row, value }: Cell<Row>) => (
        <span
          style={{
            cursor: value > 0 ? 'pointer' : 'default',
            color: value > 0 ? '#3182ce' : 'inherit',
          }}
          onClick={
            value > 0
              ? () => handleCellClick(row.original.monthIndex, 'small')
              : undefined
          }
        >
          {formatCurrency(value)}
        </span>
      ),
    },
    {
      Header: 'Medium Package',
      accessor: 'medium',
      disableSortBy: true,
      Cell: ({ row, value }: Cell<Row>) => (
        <span
          style={{
            cursor: value > 0 ? 'pointer' : 'default',
            color: value > 0 ? '#3182ce' : 'inherit',
          }}
          onClick={
            value > 0
              ? () => handleCellClick(row.original.monthIndex, 'medium')
              : undefined
          }
        >
          {formatCurrency(value)}
        </span>
      ),
    },
    {
      Header: 'Large Package',
      accessor: 'large',
      disableSortBy: true,
      Cell: ({ row, value }: Cell<Row>) => (
        <span
          style={{
            cursor: value > 0 ? 'pointer' : 'default',
            color: value > 0 ? '#3182ce' : 'inherit',
          }}
          onClick={
            value > 0
              ? () => handleCellClick(row.original.monthIndex, 'large')
              : undefined
          }
        >
          {formatCurrency(value)}
        </span>
      ),
    },
    {
      Header: 'Enterprise Package',
      accessor: 'enterprise',
      disableSortBy: true,
      Cell: ({ row, value }: Cell<Row>) => (
        <span
          style={{
            cursor: value > 0 ? 'pointer' : 'default',
            color: value > 0 ? '#3182ce' : 'inherit',
          }}
          onClick={
            value > 0
              ? () => handleCellClick(row.original.monthIndex, 'enterprise')
              : undefined
          }
        >
          {formatCurrency(value)}
        </span>
      ),
    },
    {
      Header: 'Revenue (THB)',
      accessor: 'revenue',
      disableSortBy: true,
      Cell: ({ row, value }: Cell<Row>) => (
        <span
          style={{
            cursor: value > 0 ? 'pointer' : 'default',
            color: value > 0 ? '#3182ce' : 'inherit',
          }}
          onClick={
            value > 0
              ? () => handleCellClick(row.original.monthIndex, 'all')
              : undefined
          }
        >
          {formatCurrency(value)}
        </span>
      ),
    },
  ]

  const tableData = (rows || []).map((row, index) => ({
    ...row,
    monthIndex: index + 1,
  }))

  return (
    <>
      <DataTable<Row>
        columns={columns}
        data={tableData}
        isLoading={isLoading}
        showPagination
        autoResetPage={false}
        tableSx={{ mb: 0 }}
        tableStyle={{ minH: 'auto' }}
      />

      <RevenueDetailsModal
        isOpen={isOpen}
        onClose={onClose}
        month={selectedMonth ?? 1}
        packageName={selectedPackage ?? ''}
      />
    </>
  )
}

export default BusinessOverviewTable
